import { useRef, useState } from "react";
import { ImagePlus, Video, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserAvatar } from "@/components/UserAvatar";
import { api, formatApiError, uploadFile } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export const CreatePost = ({ onCreated }) => {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [category, setCategory] = useState("none");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const imgRef = useRef();
  const vidRef = useRef();

  const submit = async () => {
    if (!text.trim() && !file) return toast.error("Write something or attach a photo/video");
    setBusy(true);
    try {
      const isVideo = file?.type.startsWith("video/");
      const url = file ? await uploadFile(file) : null;
      const payload = { type: isVideo ? "video" : file ? "image" : "text", text, images: url && !isVideo ? [url] : [], video: isVideo ? url : null, category: category === "none" ? null : category };
      const { data } = await api.post("/posts", payload);
      if (data?.id) onCreated(data);
      setText(""); setFile(null); setCategory("none");
      toast.success(isVideo ? "Video submitted — it will go live after admin approval" : "Posted to the community");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tm-card p-6" data-testid="create-post-card">
      <div className="flex gap-3">
        <UserAvatar user={user} />
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Share a tax update, compliance tip or question..." className="min-h-[90px] resize-none border-0 bg-tm-muted focus-visible:ring-1" data-testid="create-post-textarea" />
      </div>
      {file && (
        <div className="ml-14 mt-3 inline-flex items-center gap-2 rounded-full bg-tm-muted px-3 py-1 text-xs" data-testid="create-post-file-chip">
          {file.name}<button onClick={() => setFile(null)} data-testid="create-post-remove-file"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2 sm:ml-14">
        <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="create-post-image-input" />
        <input ref={vidRef} type="file" accept="video/*" hidden onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="create-post-video-input" />
        <Button variant="ghost" size="sm" onClick={() => imgRef.current.click()} data-testid="create-post-image-button"><ImagePlus className="mr-1.5 h-4 w-4 text-tm-green-light" />Photo</Button>
        <Button variant="ghost" size="sm" onClick={() => vidRef.current.click()} data-testid="create-post-video-button"><Video className="mr-1.5 h-4 w-4 text-tm-green-light" />Video</Button>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-9 w-[160px] rounded-full text-sm" data-testid="create-post-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="none">No category</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={submit} disabled={busy} className="ml-auto rounded-full bg-tm-green px-6 hover:bg-tm-green/90" data-testid="create-post-submit">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Post
        </Button>
      </div>
    </div>
  );
};
